import * as THREE from 'three';

interface BoneEnvelope { bone: number; sphere: THREE.Sphere }
interface Envelope {
  positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  sources: (THREE.BufferAttribute | THREE.InterleavedBufferAttribute)[];
  versions: number[];
  relative: boolean;
  bones: BoneEnvelope[];
  valid: boolean;
}
// Body instances own different cover indices but share the template attributes.
// Cache by those attributes so a crowd does not rescan every face morph per NPC.
const envelopes = new WeakMap<THREE.BufferAttribute | THREE.InterleavedBufferAttribute, Envelope[]>();
const point = new THREE.Vector3(), extent = new THREE.Vector3();
const lo = new THREE.Vector3(), hi = new THREE.Vector3();
const transform = new THREE.Matrix4(), sphere = new THREE.Sphere();

/** A convex skin-weight blend stays inside the union's bounding sphere. Build
 * one envelope per used joint once, then transform those small envelopes instead
 * of CPU-skinning every vertex to find bounds each frame. Morph envelopes include
 * the sum of absolute deltas, covering simultaneous positive/negative weights.
 * No bind-pose assumptions: current bone inverses also handle rig edits. */
function envelope(geometry: THREE.BufferGeometry): Envelope {
  const positions = geometry.getAttribute('position');
  const indices = geometry.getAttribute('skinIndex'), weights = geometry.getAttribute('skinWeight');
  const morphs = geometry.morphAttributes.position ?? [];
  const candidates = envelopes.get(positions) ?? [];
  const version = (attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): number =>
    attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.version : attribute.version;
  const slot = candidates.findIndex(entry => entry.sources[1] === indices && entry.sources[2] === weights
    && entry.relative === geometry.morphTargetsRelative && entry.sources.length === morphs.length + 3
    && entry.sources.every((attribute, i) => i < 3 || attribute === morphs[i - 3]));
  const cached = candidates[slot];
  if (cached && cached.sources.every((attribute, i) => version(attribute) === cached.versions[i])) return cached;
  const boxes = new Map<number, THREE.Box3>();
  let valid = !!indices && !!weights;
  for (let i = 0; valid && i < positions.count; i++) {
    point.fromBufferAttribute(positions, i); extent.set(0, 0, 0);
    for (const morph of morphs) {
      lo.fromBufferAttribute(morph, i);
      if (!geometry.morphTargetsRelative) lo.sub(point);
      extent.x += Math.abs(lo.x); extent.y += Math.abs(lo.y); extent.z += Math.abs(lo.z);
    }
    lo.copy(point).sub(extent); hi.copy(point).add(extent);
    let sum = 0;
    for (let j = 0; j < 4; j++) {
      const weight = weights.getComponent(i, j), bone = indices.getComponent(i, j);
      if (weight < 0 || !Number.isFinite(weight)) { valid = false; break; }
      sum += weight;
      if (!weight) continue;
      let box = boxes.get(bone);
      if (!box) { box = new THREE.Box3(); boxes.set(bone, box); }
      box.expandByPoint(lo); box.expandByPoint(hi);
    }
    if (Math.abs(sum - 1) > 1e-4) valid = false;
  }
  const sources = [positions, indices, weights, ...morphs].filter(Boolean);
  const result = { positions, sources, versions: sources.map(version), relative: geometry.morphTargetsRelative, valid,
    bones: Array.from(boxes, ([bone, box]) => ({ bone, sphere: box.getBoundingSphere(new THREE.Sphere()) })) };
  if (slot < 0) candidates.push(result); else candidates[slot] = result;
  envelopes.set(positions, candidates);
  return result;
}

export class BoundedSkinnedMesh extends THREE.SkinnedMesh {
  override updateMatrixWorld(force?: boolean): void {
    super.updateMatrixWorld(force);
    // The renderer computes this lazily for the first camera/shadow that needs
    // it, then all other passes reuse it until the next pose update.
    // r169 accepts null here; its declaration incorrectly omits null.
    this.boundingSphere = null!;
  }

  private readonly poseSphere = new THREE.Sphere();

  override computeBoundingSphere(): void {
    const out = this.poseSphere.makeEmpty();
    const bounds = envelope(this.geometry);
    if (!bounds.valid || this.morphTargetInfluences?.some(v => !Number.isFinite(v) || Math.abs(v) > 1)) {
      // Unusual authored weights must fail open, never remove visible geometry.
      out.set(point.set(0, 0, 0), Infinity);
    } else for (const entry of bounds.bones) {
      const bone = this.skeleton.bones[entry.bone];
      if (!bone) { out.set(point.set(0, 0, 0), Infinity); break; }
      transform.multiplyMatrices(this.bindMatrixInverse, bone.matrixWorld)
        .multiply(this.skeleton.boneInverses[entry.bone]).multiply(this.bindMatrix);
      sphere.center.copy(entry.sphere.center).applyMatrix4(transform);
      // sqrt(||A||1 * ||A||inf) bounds the spectral norm even under nonuniform
      // parent scale/shear, unlike the usual maximum-column sphere scale.
      const e = transform.elements;
      const cols = Math.max(Math.abs(e[0])+Math.abs(e[1])+Math.abs(e[2]), Math.abs(e[4])+Math.abs(e[5])+Math.abs(e[6]), Math.abs(e[8])+Math.abs(e[9])+Math.abs(e[10]));
      const rows = Math.max(Math.abs(e[0])+Math.abs(e[4])+Math.abs(e[8]), Math.abs(e[1])+Math.abs(e[5])+Math.abs(e[9]), Math.abs(e[2])+Math.abs(e[6])+Math.abs(e[10]));
      sphere.radius = entry.sphere.radius * Math.sqrt(cols * rows) + .025;
      out.union(sphere);
    }
    this.boundingSphere = out;
  }
}
